import { Assets, Texture, Spritesheet } from 'pixi.js'

export interface GameAssets {
  units: {
    opus: Record<string, Texture>
    sonnet: Record<string, Texture>
    haiku: Record<string, Texture>
  }
  buildings: {
    opus: Record<string, Texture>
    sonnet: Record<string, Texture>
    haiku: Record<string, Texture>
    shared: Record<string, Texture>
  }
  terrain: Record<string, Texture>
  ui: Record<string, Texture>
}

// Asset manifest - maps asset IDs to file paths
const ASSET_MANIFEST = {
  units: {
    opus: {
      worker: 'assets/units/opus/worker.png',
      scout: 'assets/units/opus/scout.png',
      warrior: 'assets/units/opus/warrior.png',
      archer: 'assets/units/opus/archer.png',
      reasoner: 'assets/units/opus/reasoner.png',
      hero: 'assets/units/opus/hero.png',
    },
    sonnet: {
      worker: 'assets/units/sonnet/worker.png',
      scout: 'assets/units/sonnet/scout.png',
      warrior: 'assets/units/sonnet/warrior.png',
      archer: 'assets/units/sonnet/archer.png',
      reasoner: 'assets/units/sonnet/reasoner.png',
      hero: 'assets/units/sonnet/hero.png',
    },
    haiku: {
      worker: 'assets/units/haiku/worker.png',
      scout: 'assets/units/haiku/scout.png',
      warrior: 'assets/units/haiku/warrior.png',
      archer: 'assets/units/haiku/archer.png',
      reasoner: 'assets/units/haiku/reasoner.png',
      hero: 'assets/units/haiku/hero.png',
    },
  },
  buildings: {
    opus: {
      datacenter: 'assets/buildings/opus/datacenter.png',
      barracks: 'assets/buildings/opus/barracks.png',
      research_lab: 'assets/buildings/opus/research_lab.png',
      tower: 'assets/buildings/opus/tower.png',
      wonder: 'assets/buildings/opus/wonder.png',
    },
    sonnet: {
      datacenter: 'assets/buildings/sonnet/datacenter.png',
      barracks: 'assets/buildings/sonnet/barracks.png',
      research_lab: 'assets/buildings/sonnet/research_lab.png',
      tower: 'assets/buildings/sonnet/tower.png',
      wonder: 'assets/buildings/sonnet/wonder.png',
    },
    haiku: {
      datacenter: 'assets/buildings/haiku/datacenter.png',
      barracks: 'assets/buildings/haiku/barracks.png',
      research_lab: 'assets/buildings/haiku/research_lab.png',
      tower: 'assets/buildings/haiku/tower.png',
      wonder: 'assets/buildings/haiku/wonder.png',
    },
    shared: {
      compute_farm: 'assets/buildings/compute_farm.png',
      data_mine: 'assets/buildings/data_mine.png',
      power_plant: 'assets/buildings/power_plant.png',
    },
  },
  terrain: {
    grass: 'assets/terrain/grass.png',
    forest: 'assets/terrain/forest.png',
    mountain: 'assets/terrain/mountain.png',
    water: 'assets/terrain/water.png',
    compute_node: 'assets/terrain/compute_node.png',
    data_well: 'assets/terrain/data_well.png',
    energy_crystal: 'assets/terrain/energy_crystal.png',
  },
  ui: {
    resource_compute: 'assets/ui/resource_compute.png',
    resource_data: 'assets/ui/resource_data.png',
    resource_energy: 'assets/ui/resource_energy.png',
    button_frame: 'assets/ui/button_frame.png',
    panel_background: 'assets/ui/panel_background.png',
    icon_stop: 'assets/ui/icons/stop.svg',
    icon_move: 'assets/ui/icons/move.svg',
    icon_attack: 'assets/ui/icons/attack.svg',
    icon_build: 'assets/ui/icons/build.svg',
    icon_gather: 'assets/ui/icons/gather.svg',
  },
}

class AssetLoaderClass {
  private loaded = false
  private assets: GameAssets | null = null

  async load(onProgress?: (progress: number) => void): Promise<GameAssets> {
    if (this.loaded && this.assets) {
      return this.assets
    }

    // Build flat asset list for batch loading
    const assetList: { alias: string; src: string }[] = []

    // Units
    for (const [faction, units] of Object.entries(ASSET_MANIFEST.units)) {
      for (const [unit, path] of Object.entries(units)) {
        assetList.push({ alias: `unit_${faction}_${unit}`, src: path })
      }
    }

    // Buildings
    for (const [faction, buildings] of Object.entries(ASSET_MANIFEST.buildings)) {
      for (const [building, path] of Object.entries(buildings)) {
        assetList.push({ alias: `building_${faction}_${building}`, src: path })
      }
    }

    // Terrain
    for (const [terrain, path] of Object.entries(ASSET_MANIFEST.terrain)) {
      assetList.push({ alias: `terrain_${terrain}`, src: path })
    }

    // UI
    for (const [ui, path] of Object.entries(ASSET_MANIFEST.ui)) {
      assetList.push({ alias: `ui_${ui}`, src: path })
    }

    // Add all to Assets
    for (const asset of assetList) {
      Assets.add(asset)
    }

    // Load all assets
    const totalAssets = assetList.length
    let loadedCount = 0

    const textures: Record<string, Texture> = {}

    for (const asset of assetList) {
      try {
        textures[asset.alias] = await Assets.load(asset.alias)
        loadedCount++
        if (onProgress) {
          onProgress(loadedCount / totalAssets)
        }
      } catch (e) {
        console.warn(`Failed to load asset: ${asset.alias}`, e)
      }
    }

    // Organize into GameAssets structure
    this.assets = {
      units: {
        opus: {},
        sonnet: {},
        haiku: {},
      },
      buildings: {
        opus: {},
        sonnet: {},
        haiku: {},
        shared: {},
      },
      terrain: {},
      ui: {},
    }

    // Populate units
    for (const faction of ['opus', 'sonnet', 'haiku'] as const) {
      for (const unit of Object.keys(ASSET_MANIFEST.units[faction])) {
        const key = `unit_${faction}_${unit}`
        if (textures[key]) {
          this.assets.units[faction][unit] = textures[key]
        }
      }
    }

    // Populate buildings
    for (const faction of ['opus', 'sonnet', 'haiku'] as const) {
      for (const building of Object.keys(ASSET_MANIFEST.buildings[faction])) {
        const key = `building_${faction}_${building}`
        if (textures[key]) {
          this.assets.buildings[faction][building] = textures[key]
        }
      }
    }

    // Shared buildings
    for (const building of Object.keys(ASSET_MANIFEST.buildings.shared)) {
      const key = `building_shared_${building}`
      if (textures[key]) {
        this.assets.buildings.shared[building] = textures[key]
      }
    }

    // Terrain
    for (const terrain of Object.keys(ASSET_MANIFEST.terrain)) {
      const key = `terrain_${terrain}`
      if (textures[key]) {
        this.assets.terrain[terrain] = textures[key]
      }
    }

    // UI
    for (const ui of Object.keys(ASSET_MANIFEST.ui)) {
      const key = `ui_${ui}`
      if (textures[key]) {
        this.assets.ui[ui] = textures[key]
      }
    }

    this.loaded = true
    return this.assets
  }

  getAssets(): GameAssets | null {
    return this.assets
  }

  // Helper to get unit texture by type and faction
  getUnitTexture(unitType: string, factionId: string): Texture | null {
    if (!this.assets) return null

    // Map unit type IDs to asset names
    const unitMap: Record<string, string> = {
      'WORKER': 'worker',
      'SCOUT': 'scout',
      'WARRIOR': 'warrior',
      'ARCHER': 'archer',
      'REASONER': 'reasoner',
      'HERO_OPUS': 'hero',
      'HERO_SONNET': 'hero',
      'HERO_HAIKU': 'hero',
    }

    const assetName = unitMap[unitType] || 'worker'
    const faction = factionId.toLowerCase() as 'opus' | 'sonnet' | 'haiku'

    return this.assets.units[faction]?.[assetName] || null
  }

  // Helper to get building texture by type and faction
  getBuildingTexture(buildingType: string, factionId: string): Texture | null {
    if (!this.assets) return null

    // Map building type IDs to asset names
    const buildingMap: Record<string, string> = {
      'DATACENTER': 'datacenter',
      'BARRACKS': 'barracks',
      'RESEARCH_LAB': 'research_lab',
      'TOWER': 'tower',
      'WONDER': 'wonder',
      'COMPUTE_FARM': 'compute_farm',
      'DATA_MINE': 'data_mine',
      'POWER_PLANT': 'power_plant',
      'FACTORY': 'barracks', // Use barracks sprite for factory
      'WALL': 'tower', // Use tower sprite for wall
    }

    const assetName = buildingMap[buildingType] || 'datacenter'
    const faction = factionId.toLowerCase() as 'opus' | 'sonnet' | 'haiku'

    // Check if it's a shared building
    if (['compute_farm', 'data_mine', 'power_plant'].includes(assetName)) {
      return this.assets.buildings.shared[assetName] || null
    }

    return this.assets.buildings[faction]?.[assetName] || null
  }

  // Helper to get terrain texture
  getTerrainTexture(terrainType: string | number): Texture | null {
    if (!this.assets) return null

    // Correct mapping from terrain.ts:
    // GRASS.id = 0, WATER.id = 1, MOUNTAIN.id = 2, FOREST.id = 3,
    // COMPUTE_NODE.id = 4, ENERGY_WELL.id = 5
    const terrainMap: Record<number, string> = {
      0: 'grass',          // GRASS
      1: 'water',          // WATER
      2: 'mountain',       // MOUNTAIN
      3: 'forest',         // FOREST
      4: 'compute_node',   // COMPUTE_NODE
      5: 'energy_crystal', // ENERGY_WELL (using energy_crystal asset)
    }

    const assetName = typeof terrainType === 'number'
      ? terrainMap[terrainType]
      : terrainType

    return this.assets.terrain[assetName] || null
  }
}

// Singleton instance
export const AssetLoader = new AssetLoaderClass()
